import { AmenityTypeCombobox } from '@/components/shared/AmenityTypeCombobox';
import { BusinessHoursEditor } from '@/components/shared/BusinessHoursEditor';
import { HotelCombobox } from '@/components/shared/HotelCombobox';
import { ImageUploadDropzone } from '@/components/shared/ImageUploadDropzone';
import { type AmenityFormData } from '@/hooks/useAmenityForm';
import {
  FormFieldGroup,
  FormSectionCard,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SwitchField,
  Textarea,
} from '@startupquickstart/frontend';
import { type Dispatch, type SetStateAction } from 'react';

export interface AmenityFormFieldsProps {
  formData: AmenityFormData;
  setFormData: Dispatch<SetStateAction<AmenityFormData>>;
}

/**
 * Presentational amenity form fields (General / Contact / Location / Hours).
 * Layout-agnostic so it can render inside the standalone form page or the
 * hotel-embedded form tab. All state lives in the parent via {@link useAmenityForm}.
 *
 * @param formData - Current amenity form values.
 * @param setFormData - State setter for the form values.
 */
export function AmenityFormFields({ formData, setFormData }: AmenityFormFieldsProps) {
  return (
    <>
      {/* ── General ──────────────────────────────────────────────────── */}
      <FormSectionCard
        title="General"
        description="General information about the amenity"
        contentClassName="space-y-6"
      >
          <FormFieldGroup name="name" label="Name" required>
            <Input
              id="name"
              value={formData.name}
              onChange={e => setFormData({ ...formData, name: e.target.value })}
              placeholder="Enter amenity name"
            />
          </FormFieldGroup>

          {/* Comboboxes render their own label + required marker — no FormFieldGroup
              wrapper, otherwise the field shows a duplicate label. */}
          <AmenityTypeCombobox
            label="Type"
            value={formData.amenityTypeId}
            onValueChange={value => setFormData({ ...formData, amenityTypeId: value })}
            required
            initialItem={formData.amenityType}
          />

          <HotelCombobox
            label="Hotel"
            value={formData.hotelId}
            onValueChange={value => setFormData({ ...formData, hotelId: value })}
            required
            initialItem={formData.hotel}
          />

          <FormFieldGroup name="status" label="Status">
            <Select
              value={formData.status}
              onValueChange={(value: 'published' | 'draft' | 'needs_review') =>
                setFormData({ ...formData, status: value })
              }
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="published">Published</SelectItem>
                <SelectItem value="needs_review">Needs Review</SelectItem>
              </SelectContent>
            </Select>
          </FormFieldGroup>

          <ImageUploadDropzone
            targetAspectRatio={4 / 3}
            value={formData.featuredImageId}
            onChange={value => setFormData({ ...formData, featuredImageId: value })}
          />

          <FormFieldGroup name="description" label="Description">
            <Textarea
              id="description"
              value={formData.description || ''}
              onChange={e => setFormData({ ...formData, description: e.target.value })}
              placeholder="Enter description"
              rows={4}
            />
          </FormFieldGroup>
        </FormSectionCard>

      {/* ── Contact ─────────────────────────────────────────────────── */}
      {!formData.usesHotelAddress && (
        <FormSectionCard
          title="Contact"
          description="Contact information for the amenity"
          contentClassName="grid gap-6 md:grid-cols-2"
        >
            <FormFieldGroup name="phoneNumber" label="Phone Number">
              <Input
                id="phoneNumber"
                value={formData.phoneNumber || ''}
                onChange={e => setFormData({ ...formData, phoneNumber: e.target.value })}
                placeholder="Phone Number"
              />
            </FormFieldGroup>

            <FormFieldGroup name="email" label="Email">
              <Input
                id="email"
                type="email"
                value={formData.email || ''}
                onChange={e => setFormData({ ...formData, email: e.target.value })}
                placeholder="Email Address"
              />
            </FormFieldGroup>

            <FormFieldGroup name="website" label="Website" className="md:col-span-2">
              <Input
                id="website"
                value={formData.website || ''}
                onChange={e => setFormData({ ...formData, website: e.target.value })}
                placeholder="Website URL"
              />
            </FormFieldGroup>
        </FormSectionCard>
      )}

      {/* ── Location ────────────────────────────────────────────────── */}
      <FormSectionCard
        title="Location"
        description="Location information for the amenity"
        contentClassName="space-y-6"
      >
          <SwitchField
            label="Uses Hotel Address"
            description="Use the hotel's address for this amenity instead of a separate address"
            checked={formData.usesHotelAddress}
            onCheckedChange={checked => setFormData({ ...formData, usesHotelAddress: checked })}
          />

          <div className="grid gap-6 md:grid-cols-2">
            <FormFieldGroup name="floor" label="Floor">
              <Input
                id="floor"
                value={formData.floor || ''}
                onChange={e => setFormData({ ...formData, floor: e.target.value })}
                placeholder="Floor"
              />
            </FormFieldGroup>

            <FormFieldGroup name="capacity" label="Capacity">
              <Input
                id="capacity"
                type="number"
                value={formData.capacity || ''}
                onChange={e =>
                  setFormData({ ...formData, capacity: parseInt(e.target.value) || undefined })
                }
                placeholder="Capacity"
              />
            </FormFieldGroup>
          </div>

          {!formData.usesHotelAddress && (
            <div className="grid gap-6 md:grid-cols-2">
              <FormFieldGroup name="streetAddress" label="Street Address" className="md:col-span-2">
                <Input
                  id="streetAddress"
                  value={formData.streetAddress || ''}
                  onChange={e => setFormData({ ...formData, streetAddress: e.target.value })}
                  placeholder="Street Address"
                />
              </FormFieldGroup>

              <FormFieldGroup name="streetAddress2" label="Address Line 2" className="md:col-span-2">
                <Input
                  id="streetAddress2"
                  value={formData.streetAddress2 || ''}
                  onChange={e => setFormData({ ...formData, streetAddress2: e.target.value })}
                  placeholder="Apartment, suite, etc."
                />
              </FormFieldGroup>

              <FormFieldGroup name="city" label="City">
                <Input
                  id="city"
                  value={formData.city || ''}
                  onChange={e => setFormData({ ...formData, city: e.target.value })}
                  placeholder="City"
                />
              </FormFieldGroup>

              <FormFieldGroup name="state" label="State">
                <Input
                  id="state"
                  value={formData.state || ''}
                  onChange={e => setFormData({ ...formData, state: e.target.value })}
                  placeholder="State"
                />
              </FormFieldGroup>

              <FormFieldGroup name="postalCode" label="Postal Code">
                <Input
                  id="postalCode"
                  value={formData.postalCode || ''}
                  onChange={e => setFormData({ ...formData, postalCode: e.target.value })}
                  placeholder="Postal Code"
                />
              </FormFieldGroup>

              <FormFieldGroup name="country" label="Country">
                <Input
                  id="country"
                  value={formData.country || ''}
                  onChange={e => setFormData({ ...formData, country: e.target.value })}
                  placeholder="Country"
                />
              </FormFieldGroup>
            </div>
          )}
        </FormSectionCard>

      {/* ── Hours ───────────────────────────────────────────────────── */}
      <FormSectionCard title="Hours" description="Business hours">
        <BusinessHoursEditor
          value={formData.hours}
          onChange={value =>
            setFormData({ ...formData, hours: value as Record<string, unknown> })
          }
        />
      </FormSectionCard>
    </>
  );
}
